/**
 * Relative tolerance below which two percentages are treated as equal.
 *
 * Percentages derived from the same underlying count are equal in intent but can
 * differ by ~1 ULP (relative ~1e-16) depending on how the value was computed and
 * serialized. Real frequency gaps, in contrast, are at least ~1/total (relative
 * ≳ 1e-6). 1e-9 sits safely in the gap between the two: it absorbs representation
 * noise without ever merging genuinely different percentages.
 */
export const PERCENTAGE_TIE_EPSILON = 1e-9;

/**
 * Compares two percentages in DESCENDING order, treating values that are equal
 * within a relative epsilon as a tie (returns `0`).
 *
 * This is what makes ordering deterministic across backend recomputations: without
 * the tolerance, a sub-ULP difference (e.g. `1.35e-20`) is non-zero, so an
 * `a - b || tiebreak` comparator never reaches its tiebreak and orders on pure
 * floating-point noise — which flips whenever the backend regenerates its data.
 */
export function comparePercentageDescending(
  a: number,
  b: number,
  relativeEpsilon: number = PERCENTAGE_TIE_EPSILON,
): number {
  const scale = Math.max(Math.abs(a), Math.abs(b));
  if (Math.abs(a - b) <= relativeEpsilon * scale) {
    return 0;
  }

  return b - a;
}

/**
 * Builds a comparator that ranks items by a percentage DESCENDING (noise-tolerant,
 * see {@link comparePercentageDescending}) and breaks ties by a string label
 * ASCENDING. The label tiebreak makes the order deterministic and independent of
 * the input order, so equal-ranked items never reshuffle between runs.
 */
export function createPercentageRankComparator<T>(
  percentageOf: (item: T) => number,
  labelOf: (item: T) => string,
  relativeEpsilon: number = PERCENTAGE_TIE_EPSILON,
): (a: T, b: T) => number {
  return (a, b) =>
    comparePercentageDescending(
      percentageOf(a),
      percentageOf(b),
      relativeEpsilon,
    ) || labelOf(a).localeCompare(labelOf(b));
}
